import type { CSSProperties } from 'react';

/**
 * Premium wrapping-paper patterns (CLAUDE.md §9 — premium wraps).
 *
 * Each pattern is tone-on-tone: a subtle white-alpha motif painted over the
 * creator's chosen wrap colour, so it works on ANY `themeHex`. A pattern has
 * two renderers from one definition:
 *   - drawCanvas() — baked into the tear-engine paper texture (Canvas2D).
 *   - cssBackgroundImage() — for the CSS preview surfaces (create + dashboard).
 *
 * IDs are stable keys persisted on the voucher row — never rename one, only
 * add. An empty/unknown id means "plain" (solid colour, the free default).
 */

type Ctx = CanvasRenderingContext2D;

const HEART_D =
  'M12 21.3l-1.5-1.4C5.4 15.1 2 12 2 8.2 2 5.4 4.2 3.3 6.9 3.3c1.6 0 3.1.75 4.1 1.9 1-1.15 2.5-1.9 4.1-1.9 2.7 0 4.9 2.1 4.9 4.9 0 3.8-3.4 6.9-8.5 11.7L12 21.3z';
const STAR_D = 'M12 2.6l2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8L6.6 19.3l1-6L3.2 9l6.1-.9L12 2.6z';

const white = (a: number) => `rgba(255,255,255,${a})`;

// A fixed pseudo-random scatter for confetti (deterministic → no flicker).
const rnd = (n: number) => {
  const v = Math.sin(n) * 43758.5453;
  return v - Math.floor(v);
};

/** Draw a 24-unit path motif centred at (cx, cy), scaled so its box ≈ size. */
function stampPath(ctx: Ctx, path: Path2D, cx: number, cy: number, size: number) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(size / 24, size / 24);
  ctx.translate(-12, -12);
  ctx.fill(path);
  ctx.restore();
}

// ---- Single-tone icon motifs (cats, dogs) ---------------------------------
// Multi-path line-art icons; each sub-path keeps its own fill-rule so the cat
// reads as a drawing (evenodd outline) rather than a solid blob.

// `sw` set → render the path as a stroke (line-art) of that width (in source
// viewBox units); otherwise fill it. `eo` → evenodd fill rule.
type IconPath = { d: string; eo?: boolean; sw?: number };

const CAT_VB = 24;
const CAT_PATHS: IconPath[] = [
  {
    d: 'M12.0196 14.9374C11.7284 14.9374 11.4307 14.9818 11.1784 15.0796C11.0546 15.1275 10.9032 15.2031 10.7699 15.3252C10.6361 15.4479 10.4632 15.6749 10.4632 15.9999C10.4632 16.3249 10.6361 16.5519 10.7699 16.6745C10.9032 16.7967 11.0546 16.8722 11.1784 16.9202C11.4307 17.018 11.7284 17.0624 12.0196 17.0624C12.3109 17.0624 12.6085 17.018 12.8609 16.9202C12.9846 16.8722 13.136 16.7967 13.2693 16.6745C13.4032 16.5519 13.5761 16.3249 13.5761 15.9999C13.5761 15.6749 13.4032 15.4479 13.2693 15.3252C13.136 15.2031 12.9846 15.1275 12.8609 15.0796C12.6085 14.9818 12.3109 14.9374 12.0196 14.9374Z',
    eo: true,
  },
  {
    d: 'M14.0365 12.6464C14.2015 12.38 14.5274 12.0625 15.0163 12.0625C15.5051 12.0625 15.831 12.38 15.996 12.6464C16.1681 12.9243 16.2501 13.2612 16.2501 13.5938C16.2501 13.9263 16.1681 14.2632 15.996 14.5411C15.831 14.8075 15.5051 15.125 15.0163 15.125C14.5274 15.125 14.2015 14.8075 14.0365 14.5411C13.8644 14.2632 13.7824 13.9263 13.7824 13.5938C13.7824 13.2612 13.8644 12.9243 14.0365 12.6464Z',
  },
  {
    d: 'M9.01634 12.0625C8.52751 12.0625 8.20161 12.38 8.03658 12.6464C7.86445 12.9243 7.78247 13.2612 7.78247 13.5938C7.78247 13.9263 7.86445 14.2632 8.03658 14.5411C8.20161 14.8075 8.52751 15.125 9.01634 15.125C9.50518 15.125 9.83108 14.8075 9.9961 14.5411C10.1682 14.2632 10.2502 13.9263 10.2502 13.5938C10.2502 13.2612 10.1682 12.9243 9.9961 12.6464C9.83108 12.38 9.50518 12.0625 9.01634 12.0625Z',
  },
  {
    d: 'M6.09485 4.25C5.48148 4.25 4.77463 4.42871 4.20882 4.91616C3.62226 5.4215 3.27004 6.18781 3.27004 7.1875V9.0625L3.27005 9.06545C3.2712 9.35941 3.3211 9.94757 3.4888 10.4392C3.54365 10.6001 3.63129 10.8134 3.77764 11.0058C3.49364 11.5688 3.35904 12.1495 3.29787 12.7095C3.2468 13.1771 3.24611 13.6679 3.25424 14.1211C2.5932 14.3507 1.90877 14.6349 1.5932 14.8387C1.24524 15.0634 1.14534 15.5277 1.37006 15.8756C1.59478 16.2236 2.05903 16.3235 2.40698 16.0988C2.5234 16.0236 2.86686 15.8664 3.31867 15.6939C3.38755 16.173 3.52716 16.6095 3.7221 17.0063C3.56621 17.1035 3.42847 17.1935 3.31889 17.2652C3.27694 17.2926 3.23912 17.3173 3.20599 17.3387C2.85803 17.5634 2.75813 18.0277 2.98285 18.3756C3.20757 18.7236 3.67182 18.8235 4.01978 18.5988C4.0609 18.5722 4.10473 18.5436 4.15098 18.5134C4.28216 18.4278 4.43287 18.3294 4.59701 18.2288C5.18653 18.8313 5.91865 19.2964 6.67916 19.6462C8.45998 20.4654 10.569 20.75 12.0001 20.75C13.4311 20.75 15.5402 20.4654 17.321 19.6462C18.0815 19.2964 18.8136 18.8313 19.4031 18.2288C19.5673 18.3294 19.718 18.4278 19.8491 18.5134C19.8954 18.5436 19.9392 18.5722 19.9803 18.5988C20.3283 18.8235 20.7925 18.7236 21.0173 18.3756C21.242 18.0277 21.1421 17.5634 20.7941 17.3387C20.761 17.3173 20.7232 17.2926 20.6812 17.2652C20.5716 17.1935 20.4339 17.1035 20.2781 17.0063C20.473 16.6095 20.6127 16.173 20.6815 15.6938C21.1335 15.8663 21.4771 16.0236 21.5936 16.0988C21.9415 16.3235 22.4058 16.2236 22.6305 15.8756C22.8552 15.5277 22.7553 15.0634 22.4074 14.8387C22.0917 14.6349 21.4071 14.3506 20.7459 14.121C20.7541 13.6678 20.7534 13.177 20.7023 12.7095C20.6412 12.1495 20.5065 11.5688 20.2225 11.0058C20.3689 10.8134 20.4565 10.6001 20.5114 10.4392C20.6791 9.94758 20.729 9.35941 20.7301 9.06545L20.7302 9.0625V7.18761C20.7302 6.18792 20.3779 5.42162 19.7914 4.91628C19.2256 4.42882 18.5187 4.25011 17.9054 4.25011C17.4969 4.25011 17.0744 4.40685 16.7337 4.56076C16.3726 4.72392 15.9952 4.9359 15.6558 5.13136C15.5828 5.17339 15.5119 5.21444 15.443 5.25432L15.441 5.25548C15.177 5.4084 14.9427 5.5441 14.7339 5.65167C14.6042 5.7185 14.5035 5.7643 14.4285 5.79206C14.3969 5.80377 14.3767 5.80966 14.3663 5.81242C14.1129 5.81102 13.9514 5.79033 13.7181 5.76044C13.6681 5.75403 13.6147 5.74719 13.5564 5.74003C13.2098 5.69743 12.7722 5.65636 12.0001 5.65636C11.228 5.65636 10.7905 5.69743 10.4438 5.74003C10.3855 5.74719 10.3322 5.75403 10.2821 5.76044C10.0489 5.79033 9.88738 5.81102 9.63388 5.81242C9.62352 5.80966 9.60332 5.80376 9.57174 5.79206C9.49678 5.7643 9.39604 5.71849 9.26633 5.65166C9.05755 5.54408 8.82331 5.40842 8.55926 5.25548C8.48975 5.21523 8.41818 5.17377 8.34446 5.13132C8.00502 4.93584 7.62764 4.72384 7.26652 4.56067C6.92587 4.40675 6.50329 4.25 6.09485 4.25ZM6.16192 17.6138C6.49595 17.8657 6.8808 18.0879 7.30604 18.2835C8.83694 18.9877 10.7179 19.25 12.0001 19.25C13.2823 19.25 15.1632 18.9877 16.6941 18.2835C17.1194 18.0879 17.5042 17.8657 17.8382 17.6138C17.4858 17.5524 17.2179 17.245 17.2179 16.875C17.2179 16.4608 17.5537 16.125 17.9679 16.125C18.2951 16.125 18.6295 16.2068 18.9399 16.3204C19.0985 15.9885 19.1959 15.625 19.2226 15.2271C18.9249 15.1544 18.7193 15.125 18.6134 15.125C18.1992 15.125 17.8634 14.7892 17.8634 14.375C17.8634 13.9608 18.1992 13.625 18.6134 13.625C18.8081 13.625 19.0284 13.6542 19.2504 13.6974C19.2505 13.4213 19.2415 13.1502 19.2112 12.8724C19.1407 12.227 18.958 11.6541 18.5269 11.1447C18.3727 10.9625 18.1809 10.7813 17.9402 10.6045C17.6063 10.3594 17.5344 9.88999 17.7796 9.55611C18.0247 9.22224 18.4941 9.15031 18.828 9.39546C18.9471 9.48292 19.0597 9.57282 19.1659 9.66506C19.2099 9.43686 19.2295 9.19817 19.2302 9.06087V7.18761C19.2302 6.56231 19.0238 6.23486 18.8123 6.0527C18.5801 5.85266 18.2496 5.75011 17.9054 5.75011C17.835 5.75011 17.659 5.78868 17.3513 5.92771C17.064 6.0575 16.7432 6.23612 16.4043 6.43125C16.3407 6.4679 16.2759 6.50544 16.2106 6.54328C15.9428 6.69843 15.666 6.85883 15.4209 6.98509C15.2663 7.06473 15.1052 7.14099 14.9495 7.19867C14.8058 7.25192 14.607 7.3125 14.3941 7.3125C14.0223 7.3125 13.7617 7.27877 13.5115 7.2464C13.4654 7.24043 13.4196 7.23449 13.3735 7.22883C13.0848 7.19336 12.7084 7.15636 12.0001 7.15636C11.2919 7.15636 10.9154 7.19336 10.6267 7.22883C10.5807 7.23449 10.5349 7.24042 10.4887 7.24639C10.2386 7.27877 9.97796 7.3125 9.6061 7.3125C9.39326 7.3125 9.19445 7.25191 9.05069 7.19866C8.89497 7.14098 8.73386 7.06471 8.57928 6.98506C8.33423 6.8588 8.05742 6.69839 7.78968 6.54325C7.72435 6.50539 7.65955 6.46784 7.59589 6.43118C7.25702 6.23603 6.93614 6.05741 6.64888 5.92761C6.34115 5.78856 6.16522 5.75 6.09485 5.75C5.75062 5.75 5.42007 5.85254 5.18787 6.05259C4.97643 6.23475 4.77004 6.56219 4.77004 7.1875V9.06088C4.7707 9.19819 4.79025 9.43686 4.83425 9.66506C4.94053 9.57281 5.05309 9.48292 5.1722 9.39546C5.50608 9.15031 5.97547 9.22224 6.22062 9.55612C6.46577 9.88999 6.39385 10.3594 6.05997 10.6045C5.81926 10.7813 5.62748 10.9625 5.47331 11.1447C5.04223 11.6541 4.85949 12.227 4.789 12.8724C4.75865 13.1502 4.74966 13.4213 4.74975 13.6975C4.97192 13.6543 5.19231 13.625 5.38719 13.625C5.80141 13.625 6.13719 13.9608 6.13719 14.375C6.13719 14.7892 5.80141 15.125 5.38719 15.125C5.28121 15.125 5.07549 15.1544 4.77758 15.2271C4.80434 15.625 4.90168 15.9885 5.06027 16.3203C5.37069 16.2068 5.70504 16.125 6.03224 16.125C6.44646 16.125 6.78224 16.4608 6.78224 16.875C6.78224 17.245 6.51433 17.5524 6.16192 17.6138Z',
    eo: true,
  },
];

const DOG_VB = 512;
const DOG_PATHS: IconPath[] = [
  {
    d: 'M439.464,51.073c-6.026-13.266-13.941-25.629-24.903-35.19c-10.87-9.57-25.367-15.967-41.621-15.882c-8.295-0.008-16.126,1.739-23.054,4.751c-12.177,5.3-21.367,14.101-28.414,23.535c-10.548,14.212-16.793,30.254-20.759,42.912c-1.055,3.409-1.933,6.548-2.667,9.375h-29.055h-25.984h-29.063c-2.127-8.312-5.612-19.578-11.063-31.308c-5.291-11.232-12.354-22.954-22.658-32.532c-5.148-4.768-11.164-8.978-18.11-11.983c-6.929-3.012-14.768-4.76-23.055-4.751c-16.16-0.085-30.615,6.135-41.519,15.544C81.093,29.739,71.313,49.95,64.908,70.853c-6.321,20.987-9.013,42.945-9.03,61.696c0,28.591,0,140.346,0,181.932c0.008,15.916,2.482,35.696,8.616,56.894c9.215,31.713,26.785,66.751,57.671,94.21c30.81,27.494,75.021,46.515,133.831,46.414c39.215,0.016,71.933-8.312,98.523-22.144c39.966-20.742,65.544-53.434,80.802-86.068c15.3-32.768,20.759-65.485,20.801-89.307c0-41.586,0-153.341,0-181.932C456.046,108.355,451.397,77.688,439.464,51.073z M424.933,314.481c0.008,12.666-2.068,29.924-7.384,48.202c-7.966,27.493-23.106,57.148-48.414,79.569c-25.375,22.397-60.844,38.456-113.139,38.557c-34.86-0.016-62.236-7.274-84.135-18.625c-32.768-17.029-53.789-43.67-66.945-71.636c-8.506-18.059-13.468-36.625-15.907-52.675c12.565,0,34.473,0,64.574,0c49.443,0,82.405-47.586,82.405-86.042c0-29.578,0-106.228,0-140.076h7.021h25.984h54.869l2.109-13.097c0.025-0.414,3.392-19.536,11.797-37.012c4.144-8.735,9.528-16.92,15.469-22.27c2.979-2.7,6.033-4.725,9.198-6.076c3.198-1.35,6.523-2.102,10.506-2.11c8.439,0.084,14.734,2.768,21.173,8.245c9.553,8.118,17.908,23.586,23.114,41.198c5.308,17.518,7.73,36.928,7.706,51.915C424.933,161.14,424.933,272.895,424.933,314.481z',
  },
  {
    d: 'M253.397,350.86c-22.963,0-41.579,13.975-41.579,31.19c0,17.224,18.616,31.19,41.579,31.19c22.97,0,41.586-13.966,41.586-31.19C294.984,364.835,276.368,350.86,253.397,350.86z',
  },
  { d: 'M307.979,249.502a25.992,25.992 0 1,0 51.984,0a25.992,25.992 0 1,0 -51.984,0z' },
  {
    d: 'M372.08,71.039c-17.316,0-20.793,36.388-20.793,36.388h38.126C389.413,107.426,389.396,71.039,372.08,71.039z',
  },
];

const BALLOON_VB = 24;
const BALLOON_PATHS: IconPath[] = [
  {
    d: 'M12.1296 17.8108L10 21H15L12.8704 17.8108M9.5 9.5C9.5 7.84315 10.3431 6.5 12 6.5M19 9C19 15 12.5 18 12.5 18C12.5 18 6 15 6 9C6 4.5 10 3 12.5 3C15 3 19 4.5 19 9Z',
    sw: 2,
  },
];

const SNOWFLAKE_VB = 24;
const SNOWFLAKE_PATHS: IconPath[] = [
  { d: 'M12 2V18M12 22V18M12 18L15 21M12 18L9 21M15 3L12 6L9 3', sw: 1.5 },
  {
    d: 'M3.33978 7.00042L6.80389 9.00042M6.80389 9.00042L17.1962 15.0004M6.80389 9.00042L5.70581 4.90234M6.80389 9.00042L2.70581 10.0985M17.1962 15.0004L20.6603 17.0004M17.1962 15.0004L21.2943 13.9023M17.1962 15.0004L18.2943 19.0985',
    sw: 1.5,
  },
  {
    d: 'M20.66 7.00042L17.1959 9.00042M17.1959 9.00042L6.80364 15.0004M17.1959 9.00042L18.294 4.90234M17.1959 9.00042L21.294 10.0985M6.80364 15.0004L3.33954 17.0004M6.80364 15.0004L2.70557 13.9023M6.80364 15.0004L5.70557 19.0985',
    sw: 1.5,
  },
];

const PAW_VB = 256;
const PAW_PATHS: IconPath[] = [
  {
    d: 'M136,60a28,28,0,1,1,28,28A28.03146,28.03146,0,0,1,136,60ZM72,108a28,28,0,1,0-28,28A28.03146,28.03146,0,0,0,72,108ZM92,88A28,28,0,1,0,64,60,28.03146,28.03146,0,0,0,92,88Zm95.0918,60.84473a35.3317,35.3317,0,0,1-16.8418-21.124,43.99839,43.99839,0,0,0-84.5-.00439,35.2806,35.2806,0,0,1-16.7998,21.105,40.00718,40.00718,0,0,0,34.57226,72.05176,64.08634,64.08634,0,0,1,48.86524-.03711,40.0067,40.0067,0,0,0,34.7041-71.99121ZM212,80a28,28,0,1,0,28,28A28.03146,28.03146,0,0,0,212,80Z',
  },
];

const SMILEY_VB = 24;
const SMILEY_PATHS: IconPath[] = [
  {
    d: 'M12 23.25C18.2132 23.25 23.25 18.2132 23.25 12C23.25 5.7868 18.2132 0.75 12 0.75C5.7868 0.75 0.75 5.7868 0.75 12C0.75 18.2132 5.7868 23.25 12 23.25Z',
    sw: 1.5,
  },
  {
    d: 'M18.75 10.5005C18.595 10.0617 18.3077 9.68178 17.9278 9.41305C17.5478 9.14432 17.0939 9.00001 16.6285 9.00001C16.1631 9.00001 15.7092 9.14432 15.3292 9.41305C14.9551 9.67765 14.6709 10.0501 14.5142 10.4803',
    sw: 1.5,
  },
  {
    d: 'M5.25018 10.5005C5.4052 10.0617 5.69247 9.68178 6.07242 9.41305C6.45237 9.14432 6.9063 9.00001 7.37168 9.00001C7.83706 9.00001 8.29099 9.14432 8.67094 9.41305C9.03808 9.67272 9.31868 10.0362 9.47705 10.4563',
    sw: 1.5,
  },
  {
    d: 'M5.94824 15C6.50681 16.1273 7.3692 17.076 8.43818 17.7394C9.50715 18.4027 10.7402 18.7542 11.9982 18.7542C13.2563 18.7542 14.4893 18.4027 15.5583 17.7394C16.6202 17.0804 17.4782 16.1399 18.0371 15.0224',
    sw: 1.5,
  },
];

const FLOWER_VB = 512;
const FLOWER_PATHS: IconPath[] = [
  {
    d: 'M437.46,157.012c0-9.442-1.466-19.036-4.525-28.464c-6.313-19.42-18.534-35.384-33.98-46.418c-15.447-11.033-34.156-17.214-53.59-17.221c-0.475,0-0.957,0.035-1.438,0.042c-5.308-17.235-15.475-32.333-28.967-43.59C299.01,8.038,278.388-0.007,255.993,0c-22.395-0.007-43.017,8.038-58.96,21.362c-13.485,11.257-23.659,26.354-28.974,43.59c-0.475-0.007-0.963-0.042-1.438-0.042c-19.428,0.007-38.142,6.18-53.59,17.221c-15.447,11.027-27.668,26.998-33.967,46.418c-3.066,9.428-4.525,19.023-4.525,28.457c0.014,29.881,14.651,58.031,39.12,75.258c-3.318,9.693-4.952,19.763-4.952,29.764c0.007,14.224,3.282,28.331,9.672,41.159c6.382,12.829,15.894,24.379,28.268,33.352c16.299,11.851,35.28,17.591,54.008,17.584c15.112,0,30.084-3.785,43.534-10.992v82.556c-5.684-8.219-12.85-15.768-21.397-22.207c-16.886-12.688-36.404-19.05-55.168-19.064c-14.763,0-29.163,3.98-41.16,12.178l-3.114,2.13l-1.188,3.589c-2.276,6.934-3.372,14.17-3.372,21.439c0.048,25.559,13.275,51.508,36.732,69.183c16.878,12.689,36.404,19.05,55.161,19.064c14.762,0,29.162-3.981,41.159-12.179l2.242-1.53h0.175l2.234,1.53c11.997,8.198,26.39,12.179,41.16,12.179c18.757-0.014,38.282-6.376,55.168-19.064c23.45-17.675,36.676-43.624,36.732-69.183c0-7.27-1.096-14.505-3.379-21.439l-1.187-3.589l-3.115-2.13c-11.997-8.198-26.397-12.178-41.16-12.178c-18.757,0.014-38.282,6.376-55.168,19.064c-7.388,5.573-13.757,11.963-19.022,18.904v-79.972c13.778,7.661,29.218,11.711,44.805,11.711c18.736,0.006,37.709-5.734,54.016-17.584c12.367-8.98,21.879-20.524,28.261-33.352c6.39-12.835,9.672-26.942,9.672-41.166c0-10-1.641-20.063-4.951-29.756C422.796,215.043,437.447,186.893,437.46,157.012z M235.972,483.41c-7.262,4.079-15.943,6.257-25.286,6.264c-13.68,0.007-28.632-4.714-41.746-14.581c-18.276-13.702-27.878-33.653-27.822-51.341c0-3.701,0.413-7.298,1.222-10.748c7.263-4.078,15.943-6.257,25.287-6.264c13.687-0.007,28.638,4.713,41.746,14.581c18.275,13.708,27.877,33.646,27.821,51.341C237.194,476.357,236.782,479.96,235.972,483.41z M298.969,421.322c13.108-9.868,28.059-14.588,41.746-14.581c9.344,0.006,18.024,2.185,25.287,6.264c0.803,3.45,1.222,7.054,1.222,10.748c0.056,17.688-9.546,37.64-27.821,51.341c-13.115,9.867-28.059,14.588-41.746,14.581c-9.344-0.007-18.024-2.186-25.28-6.264c-0.824-3.45-1.229-7.053-1.229-10.747C271.091,454.968,280.693,435.03,298.969,421.322z M379.122,218.032l-9.148,5.049l4.428,9.462c4.406,9.4,6.55,19.463,6.55,29.477c0,10.789-2.493,21.508-7.333,31.215c-4.832,9.706-11.984,18.401-21.397,25.244c-12.409,9.009-26.676,13.31-40.894,13.317c-17.374-0.007-34.56-6.481-47.703-18.785l-7.633-7.144l-7.626,7.144c-13.143,12.304-30.322,18.778-47.71,18.785c-14.218-0.014-28.485-4.308-40.887-13.317c-9.42-6.843-16.57-15.538-21.403-25.238c-4.832-9.706-7.332-20.426-7.332-31.215c0-10.014,2.151-20.078,6.55-29.477l4.427-9.463l-9.148-5.048c-22.304-12.291-36.013-35.964-36.006-61.034c0.007-7.13,1.104-14.378,3.442-21.564c4.798-14.769,13.995-26.76,25.72-35.146c11.718-8.373,25.922-13.059,40.601-13.059c2.835,0,5.691,0.175,8.554,0.531l10.357,1.292l1.983-10.258c3.094-16.062,11.711-30.189,23.828-40.3c12.123-10.112,27.619-16.167,44.651-16.174c17.032,0.007,32.535,6.062,44.651,16.174c12.123,10.112,20.74,24.239,23.827,40.3l1.983,10.258l10.363-1.292c2.856-0.356,5.713-0.531,8.548-0.531c14.679,0,28.897,4.686,40.615,13.059c11.718,8.386,20.908,20.384,25.712,35.154c2.333,7.186,3.436,14.428,3.436,21.564C415.143,182.082,401.434,205.748,379.122,218.032z',
  },
  {
    d: 'M255.993,119.994c-36.467,0-66.034,29.567-66.034,66.04s29.567,66.041,66.034,66.041c36.481,0,66.048-29.568,66.048-66.041S292.474,119.994,255.993,119.994z',
  },
];

// Flower3 is a 2-tone illustration; we keep only its petal shapes (the white
// layer) as tone-on-tone fills and drop the black under-layer.
const FLOWER3_VB = 183.79;
const FLOWER3_PATHS: IconPath[] = [
  {
    d: 'M117.18,87.11c-1.28-5.56-4.33-8.84-7.7-12-3.2-3-3-3.94-.47-8,2.39-3.81,5.87-7,6.66-11.73,6.6-8.17,12.59-16.82,23.84-19.66,6.58-1.65,12.44-.42,18.27,2a10.26,10.26,0,0,1,5,4.44c3.26,6.55,4.89,13.36,1.18,20.45-5,9.48-11.72,16.78-22.59,19.35-2.5.59-4.91,1.54-7.42,2.06C128.69,85.06,123.39,86,117.18,87.11Z',
  },
  {
    d: 'M62.59,96.1A86.13,86.13,0,0,0,66,104.71c1.3,2.51,3.17,4.72,5.08,7.5a37.55,37.55,0,0,1-2.26,5.06,79.58,79.58,0,0,1-14.2,17,26.18,26.18,0,0,1-7.05,4.66c-7.55,3.23-15,7.2-23.64,6.14a74.77,74.77,0,0,1-7.51-1.45c-3.94-.91-5.52-3.68-5.68-7.51A27.87,27.87,0,0,1,13.6,122c5-9.79,12.44-17.18,22.46-21.39,7.53-3.17,15.29-6.46,23.9-5.2A26.55,26.55,0,0,1,62.59,96.1Z',
  },
  {
    d: 'M94.74,67c-8.38.94-11.06-.23-14.16-6.76-3.82-8-6.93-16.36-8.21-25.22-1.45-10,3.23-17.63,10-24.33a5.15,5.15,0,0,1,1.42-1c6.32-3,18.74.87,22.19,7.05a9.7,9.7,0,0,1,1.45,4.83c-.28,6.53.11,13.12-2.43,19.45-2.77,6.9-5,14-7.59,21A37.26,37.26,0,0,1,94.74,67Z',
  },
  {
    d: 'M111.09,107.39l5.09-10.31c2.39,0,4.75-.44,6.89.07C134.77,99.94,146,103.91,156,111c5.45,3.84,8.49,9.1,10.6,15.12a7.53,7.53,0,0,1-2.24,8.54,15.59,15.59,0,0,1-15,3.92,54.73,54.73,0,0,1-29.26-18.11C116.72,116.51,113.14,112.86,111.09,107.39Z',
  },
  {
    d: 'M99.58,116.65c6.18,9.54,9.21,19.58,8.35,30.82-.52,6.85-1.08,13.6-3.75,20.08-1.59,3.85-3.35,7.82-7.75,8.2-4,.34-7.69-1.9-10.37-5.1a23.93,23.93,0,0,1-2.64-4.38c-8.37-16.06-5.88-31.93.69-48.23Z',
  },
  {
    d: 'M74.35,70.31,66.6,81.61C64,83.16,61.49,82.8,59,82.09c-6.56-1.9-13.18-3.69-19.62-6-5.61-2-8.8-7-12.68-11.16-3.35-3.55-4.54-7.75-4.35-12.53.27-7,5-10.9,12-9.66a11.73,11.73,0,0,1,2.46.71c6.8,2.89,13.73,5.53,20.31,8.85,3,1.49,5.42,4.16,7.73,6.63C68.1,62.5,71.05,66.35,74.35,70.31Z',
  },
  {
    d: 'M105.72,90.15c-.28,4.6-3.29,8.73-6.76,12.55-5,5.51-11.91,6.69-18.52,3.34-4-2-6-5.38-5.59-9.67a49.37,49.37,0,0,1,2.67-10.69C79.6,79.57,85.15,75.42,92,76.36c3.41.47,6.71.5,9.41,3.14A13.48,13.48,0,0,1,105.72,90.15Z',
  },
];

// Interlocking wedding bands (from assets/wedding-rings.svg). Single fill path;
// evenodd cuts the band holes so the rings read as outlines, not discs.
const RINGS_VB = 512;
const RINGS_PATHS: IconPath[] = [
  {
    d: 'M138.091 61.5535C141.491 61.294 146.016 61.4117 149.491 61.4116L168.14 61.4204L189.632 61.4115C193.616 61.4113 197.66 61.2732 201.638 61.4873C203.406 61.5825 205.389 61.7055 207.001 62.4833C211.068 64.4451 226.841 79.8972 228.175 83.9255C228.916 86.1611 229.066 89.0533 228.452 91.3188C227.185 95.9935 223.115 102.864 220.823 107.45L204.9 139.264C226.27 143.088 250.17 154.771 267.305 167.875C300.492 193.225 322.199 230.762 327.618 272.169C331.981 305.357 325.78 341.952 309.215 371.136C304.629 379.216 300.554 384.855 294.971 392.141C299.651 393.765 305.244 395.321 310.045 396.711C314.524 398.007 321.77 400.562 326.13 401.081C343.998 403.204 362.682 401.263 379.442 395.042C406.619 384.899 428.66 364.386 440.725 338.007C452.629 311.788 453.718 281.934 443.757 254.917C433.285 227.449 412.361 205.245 385.562 193.163C368.376 185.379 348.993 181.808 330.15 183.525C327.182 183.796 321.967 184.474 319.166 185.528C306.449 169.776 296.46 158.469 280.317 146.399C282.975 145.835 291.609 142.589 295.045 141.552C307.519 137.7 320.41 135.356 333.443 134.572C377.029 132.252 416.511 148.183 448.652 177.096C466.471 193.711 480.264 214.174 488.979 236.926C503.813 276.183 502.407 319.73 485.074 357.949C467.662 396.219 435.746 425.993 396.36 440.708C359.992 454.342 321.579 453.646 285.304 440.213C276.902 437.101 269.226 435.376 261.741 429.936C260.357 428.929 257.292 427.131 255.818 426.5C252.516 427.902 246.247 431.751 242.376 433.645C230.799 439.308 218.623 443.655 206.077 446.603C165.451 455.958 122.773 448.855 87.3672 426.845C51.6951 404.726 26.3202 369.29 16.8678 328.395C7.36126 287.473 14.5879 244.449 36.9426 208.879C60.1522 171.984 94.5708 148.868 136.759 139.254C129.702 126.264 123.753 112.135 116.672 99.0906C115.438 96.8172 113.668 93.4189 113.17 90.9198C112.473 87.5628 113.144 84.0664 115.035 81.2061C117.14 78.0192 128.988 66.6369 132.745 63.5281C134.045 62.4519 136.405 61.8929 138.091 61.5535ZM217.015 389.658C191.892 361.868 178.833 325.239 180.712 287.824C180.724 287.507 180.739 287.191 180.757 286.874C182.226 259.828 189.577 238.564 202.419 214.774C219.415 221.608 230.837 231.344 241.366 246.197C239.164 251.181 236.67 256.758 235.348 262.079C229.931 283.869 230.033 309.872 238.046 331C241.484 340.064 250.201 352.232 256.021 360.435C275.22 333.873 284.159 307.74 278.836 274.654C274.27 246.228 258.62 220.771 235.317 203.863C218.714 191.8 199.018 184.715 178.535 183.439C174.594 183.164 168.658 182.931 164.71 183.352C158.149 183.901 153.616 184.336 147.144 185.713C123.663 190.712 102.523 203.407 87.0772 221.785C68.2711 244.216 59.2104 273.232 61.911 302.378C64.4014 331.258 78.3053 357.952 100.542 376.546C122.47 394.878 150.744 403.82 179.226 401.43C191.139 400.354 206.561 395.533 217.015 389.658Z',
    eo: true,
  },
];

/** Tile a multi-path icon motif across the canvas, staggered, upright. */
function tileIcon(ctx: Ctx, w: number, h: number, vb: number, paths: IconPath[], cols: number, op: number) {
  const tile = w / cols;
  const size = tile * 0.62;
  const cached = paths.map((p) => ({ path: new Path2D(p.d), eo: p.eo, sw: p.sw }));
  ctx.fillStyle = white(op);
  ctx.strokeStyle = white(op);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  let row = 0;
  for (let y = tile * 0.5; y < h + tile; y += tile, row++) {
    const off = row % 2 ? tile / 2 : 0;
    for (let x = off; x < w + tile; x += tile) {
      ctx.save();
      ctx.translate(x - size / 2, y - size / 2);
      ctx.scale(size / vb, size / vb);
      for (const { path, eo, sw } of cached) {
        if (sw) {
          ctx.lineWidth = sw;
          ctx.stroke(path);
        } else {
          ctx.fill(path, eo ? 'evenodd' : 'nonzero');
        }
      }
      ctx.restore();
    }
  }
}

/** A 48-unit CSS tile with two staggered, upright copies of an icon motif. */
function iconTileSvg(vb: number, paths: IconPath[], op: number): string {
  const sc = (22 / vb).toFixed(4);
  const pathSvg = (p: IconPath) =>
    p.sw
      ? `<path d='${p.d}' fill='none' stroke='${white(op)}' stroke-width='${p.sw}' stroke-linecap='round' stroke-linejoin='round'/>`
      : `<path d='${p.d}' fill='${white(op)}'${p.eo ? " fill-rule='evenodd'" : ''}/>`;
  const draw = (tx: number, ty: number) =>
    `<g transform='translate(${tx} ${ty}) scale(${sc})'>${paths.map(pathSvg).join('')}</g>`;
  return svgUrl(`<g>${draw(2, 3)}${draw(24, 25)}</g>`, 48);
}

export interface WrapPattern {
  id: string;
  title: string;
  tier: 'premium';
  /** Paint the repeating motif over a canvas already filled with the wrap colour. */
  drawCanvas: (ctx: Ctx, w: number, h: number) => void;
  /** CSS `background-image` value (a data-URI SVG or a gradient). */
  cssBackgroundImage: () => string;
  /** CSS `background-size`, when the image is a tile (omitted for gradients). */
  cssBackgroundSize?: string;
}

function svgUrl(inner: string, size = 40): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>${inner}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export const WRAP_PATTERNS: ReadonlyArray<WrapPattern> = [
  {
    id: 'dots',
    title: 'Polka',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const tile = Math.max(28, Math.round(w / 12));
      const r = tile * 0.13;
      ctx.fillStyle = white(0.24);
      let row = 0;
      for (let y = 0; y < h + tile; y += tile / 2, row++) {
        const off = row % 2 ? tile / 2 : 0;
        for (let x = -tile; x < w + tile; x += tile) {
          ctx.beginPath();
          ctx.arc(x + off, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    },
    cssBackgroundImage: () =>
      svgUrl(`<g fill='${white(0.26)}'><circle cx='10' cy='10' r='4'/><circle cx='30' cy='30' r='4'/></g>`),
    cssBackgroundSize: '26px 26px',
  },
  {
    id: 'stripes',
    title: 'Stripes',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const band = Math.max(22, Math.round(w / 16));
      ctx.save();
      ctx.strokeStyle = white(0.14);
      ctx.lineWidth = band * 0.5;
      ctx.beginPath();
      for (let x = -h; x < w + h; x += band * 1.7) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x + h, h);
      }
      ctx.stroke();
      ctx.restore();
    },
    cssBackgroundImage: () =>
      `repeating-linear-gradient(45deg, ${white(0.14)} 0 7px, transparent 7px 20px)`,
  },
  {
    id: 'hearts',
    title: 'Hearts',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const heart = new Path2D(HEART_D);
      const tile = Math.max(34, Math.round(w / 9));
      const size = tile * 0.5;
      ctx.fillStyle = white(0.22);
      let row = 0;
      for (let y = tile * 0.4; y < h + tile; y += tile, row++) {
        const off = row % 2 ? tile / 2 : 0;
        for (let x = off; x < w + tile; x += tile) {
          stampPath(ctx, heart, x, y, size);
        }
      }
    },
    cssBackgroundImage: () =>
      svgUrl(
        `<g fill='${white(0.24)}'>` +
          `<path transform='translate(4 5) scale(0.5)' d='${HEART_D}'/>` +
          `<path transform='translate(24 25) scale(0.5)' d='${HEART_D}'/>` +
          `</g>`,
      ),
    cssBackgroundSize: '34px 34px',
  },
  {
    id: 'stars',
    title: 'Stars',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const star = new Path2D(STAR_D);
      const tile = Math.max(34, Math.round(w / 9));
      const size = tile * 0.52;
      ctx.fillStyle = white(0.24);
      let row = 0;
      for (let y = tile * 0.4; y < h + tile; y += tile, row++) {
        const off = row % 2 ? tile / 2 : 0;
        for (let x = off; x < w + tile; x += tile) {
          stampPath(ctx, star, x, y, size);
        }
      }
    },
    cssBackgroundImage: () =>
      svgUrl(
        `<g fill='${white(0.26)}'>` +
          `<path transform='translate(4 4) scale(0.52)' d='${STAR_D}'/>` +
          `<path transform='translate(24 24) scale(0.52)' d='${STAR_D}'/>` +
          `</g>`,
      ),
    cssBackgroundSize: '34px 34px',
  },
  {
    id: 'confetti',
    title: 'Confetti',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const n = Math.round((w * h) / 9000);
      for (let i = 0; i < n; i++) {
        const x = rnd(i * 1.3 + 0.5) * w;
        const y = rnd(i * 2.7 + 1.1) * h;
        const a = rnd(i * 0.7 + 4) * Math.PI;
        const len = 7 + rnd(i * 3.3) * 7;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(a);
        ctx.fillStyle = white(0.18 + rnd(i * 5.1) * 0.12);
        ctx.beginPath();
        ctx.roundRect(-len / 2, -1.6, len, 3.2, 1.6);
        ctx.fill();
        ctx.restore();
      }
    },
    cssBackgroundImage: () =>
      svgUrl(
        `<g fill='none' stroke='${white(0.28)}' stroke-width='3' stroke-linecap='round'>` +
          `<path d='M6 8l5 4'/><path d='M30 6l-4 5'/><path d='M9 30l4-4'/>` +
          `<path d='M32 30l-5-3'/><path d='M20 18l3 4'/></g>`,
      ),
    cssBackgroundSize: '40px 40px',
  },
  {
    id: 'cats',
    title: 'Cats',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, CAT_VB, CAT_PATHS, 6, 0.27),
    cssBackgroundImage: () => iconTileSvg(CAT_VB, CAT_PATHS, 0.3),
    cssBackgroundSize: '48px 48px',
  },
  {
    id: 'dogs',
    title: 'Dogs',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, DOG_VB, DOG_PATHS, 6, 0.24),
    cssBackgroundImage: () => iconTileSvg(DOG_VB, DOG_PATHS, 0.26),
    cssBackgroundSize: '48px 48px',
  },
  {
    id: 'florals',
    title: 'Floral',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, FLOWER_VB, FLOWER_PATHS, 6, 0.26),
    cssBackgroundImage: () => iconTileSvg(FLOWER_VB, FLOWER_PATHS, 0.28),
    cssBackgroundSize: '48px 48px',
  },
  {
    id: 'blossom',
    title: 'Blossom',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, FLOWER3_VB, FLOWER3_PATHS, 6, 0.26),
    cssBackgroundImage: () => iconTileSvg(FLOWER3_VB, FLOWER3_PATHS, 0.28),
    cssBackgroundSize: '48px 48px',
  },
  {
    id: 'balloons',
    title: 'Balloons',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, BALLOON_VB, BALLOON_PATHS, 5, 0.3),
    cssBackgroundImage: () => iconTileSvg(BALLOON_VB, BALLOON_PATHS, 0.34),
    cssBackgroundSize: '46px 46px',
  },
  {
    id: 'snowflakes',
    title: 'Snowflakes',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, SNOWFLAKE_VB, SNOWFLAKE_PATHS, 6, 0.3),
    cssBackgroundImage: () => iconTileSvg(SNOWFLAKE_VB, SNOWFLAKE_PATHS, 0.34),
    cssBackgroundSize: '46px 46px',
  },
  {
    id: 'paws',
    title: 'Paws',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, PAW_VB, PAW_PATHS, 7, 0.22),
    cssBackgroundImage: () => iconTileSvg(PAW_VB, PAW_PATHS, 0.26),
    cssBackgroundSize: '44px 44px',
  },
  {
    id: 'smiley',
    title: 'Smiley',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, SMILEY_VB, SMILEY_PATHS, 6, 0.32),
    cssBackgroundImage: () => iconTileSvg(SMILEY_VB, SMILEY_PATHS, 0.36),
    cssBackgroundSize: '46px 46px',
  },
  {
    id: 'rings',
    title: 'Rings',
    tier: 'premium',
    drawCanvas: (ctx, w, h) => tileIcon(ctx, w, h, RINGS_VB, RINGS_PATHS, 6, 0.24),
    cssBackgroundImage: () => iconTileSvg(RINGS_VB, RINGS_PATHS, 0.28),
    cssBackgroundSize: '46px 46px',
  },
  {
    id: 'lace',
    title: 'Lace',
    tier: 'premium',
    drawCanvas(ctx, w, h) {
      const band = Math.max(14, Math.round(w / 22));
      ctx.strokeStyle = white(0.12);
      ctx.lineWidth = Math.max(1, Math.round(w / 700));
      ctx.beginPath();
      for (let x = -h; x < w + h; x += band) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x + h, h);
        ctx.moveTo(x, 0);
        ctx.lineTo(x - h, h);
      }
      ctx.stroke();
    },
    cssBackgroundImage: () =>
      `repeating-linear-gradient(45deg, ${white(0.13)} 0 1px, transparent 1px 9px), repeating-linear-gradient(-45deg, ${white(0.13)} 0 1px, transparent 1px 9px)`,
  },
];

export const WRAP_PATTERN_IDS = WRAP_PATTERNS.map((p) => p.id);

export function getWrapPattern(id: string | undefined | null): WrapPattern | null {
  if (!id) return null;
  return WRAP_PATTERNS.find((p) => p.id === id) ?? null;
}

/**
 * A React style for a mini wrapped-gift surface: the wrap colour plus the
 * pattern overlay (if any). Used by the create preview + dashboard thumbnail.
 */
export function wrapPatternStyle(themeHex: string, patternId: string | undefined | null): CSSProperties {
  const pattern = getWrapPattern(patternId);
  if (!pattern) return { backgroundColor: themeHex };
  return {
    backgroundColor: themeHex,
    backgroundImage: pattern.cssBackgroundImage(),
    ...(pattern.cssBackgroundSize ? { backgroundSize: pattern.cssBackgroundSize } : {}),
  };
}

/**
 * The pattern motif as a `data:` image href, for use in an SVG `<pattern>` tile
 * (e.g. to paint the pattern onto an illustrated gift). Returns null when there
 * is no image form — plain (no pattern) or the gradient-based "stripes".
 */
export function wrapPatternImageHref(patternId: string | undefined | null): string | null {
  const pattern = getWrapPattern(patternId);
  if (!pattern) return null;
  const bg = pattern.cssBackgroundImage();
  if (!bg.startsWith('url("')) return null; // gradient (stripes) — no image form
  return bg.slice(5, -2); // strip url(" … ")
}
